import React, { useState } from 'react';
import { Download, FileText, AlertCircle, Check, Info } from 'lucide-react';
import { Button } from '../ui/Button';

const FinancialAdvisorCRMTemplate: React.FC = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = () => {
    setIsDownloading(true);
    
    // Create CSV content
    const headers = [
      'Client_ID*',
      'First_Name*',
      'Last_Name*',
      'Date_of_Birth*',
      'Email*',
      'Phone*',
      'Street_Address*',
      'City*',
      'State*',
      'ZIP*',
      'Account_Type*',
      'Investment_Goals*',
      'Risk_Tolerance*',
      'Annual_Income_Range*',
      'Net_Worth*',
      'Portfolio_Value*',
      'Tax_Filing_Status*',
      'Financial_Advisor_Name*',
      'Last_Contact_Date*',
      'Next_Follow_Up*',
      'Client_Status*',
      'Beneficiaries',
      'Account_Roles',
      'Tags',
      'Client_Notes'
    ];

    // Sample data row
    const sampleData = [
      'FA12345',
      'John',
      'Smith',
      '01/15/1975',
      'john.smith@example.com',
      '555-123-4567',
      '123 Main Street',
      'Anytown',
      'CA',
      '90210',
      'Individual',
      'Retirement,Education',
      'Moderate',
      '$100,000-$150,000',
      '$750,000',
      '$350,000',
      'Married_Joint',
      'Jane Advisor',
      '05/15/2023',
      '08/15/2023',
      'Active',
      'Sarah Smith, Michael Smith',
      'Primary',
      'VIP,Referral,HighNetWorth',
      'Client prefers morning meetings. Annual review scheduled for October.'
    ];

    // Create CSV content
    const csvContent = [
      headers.join(','),
      sampleData.join(',')
    ].join('\n');

    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', 'financial_advisor_crm_template.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    setTimeout(() => {
      setIsDownloading(false);
    }, 1500);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-sm">
      <div className="flex items-start mb-6">
        <FileText className="h-8 w-8 text-primary mr-3 flex-shrink-0" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Advisor CRM Data Import Template</h1>
          <p className="text-gray-600 mt-1">
            Standardized format for importing client data into the CRM system
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
        <div className="flex">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">Instructions</h3>
            <p className="mt-1 text-sm text-blue-700">
              Please follow this standardized format for importing client data into the CRM system. 
              All required fields must be completed accurately to ensure successful data migration.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Template Structure</h2>
          <p className="text-gray-700 mb-4">
            Download this Excel-compatible CSV template with the following features:
          </p>
          <ul className="space-y-2">
            {[
              'Pre-formatted columns with data validation',
              'Drop-down menus for all selection fields',
              'Built-in format checking for dates, currency, and contact information',
              'Color-coded required vs. optional fields',
              'Sample row with example data'
            ].map((feature, index) => (
              <li key={index} className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Required Fields</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-md font-medium text-gray-800 mb-2">1. Client Identification</h3>
                <ul className="space-y-1 text-gray-700">
                  <li>• Client_ID [FA12345]</li>
                  <li>• First_Name</li>
                  <li>• Last_Name</li>
                  <li>• Date_of_Birth [MM/DD/YYYY]</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-md font-medium text-gray-800 mb-2">2. Contact Information</h3>
                <ul className="space-y-1 text-gray-700">
                  <li>• Email [name@domain.com]</li>
                  <li>• Phone [XXX-XXX-XXXX]</li>
                  <li>• Street_Address</li>
                  <li>• City</li>
                  <li>• State [US State Code]</li>
                  <li>• ZIP [5 or 9 digits]</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-md font-medium text-gray-800 mb-2">3. Financial Details</h3>
                <ul className="space-y-1 text-gray-700">
                  <li>• Account_Type [Individual/Joint/Trust/Retirement/Corporate]</li>
                  <li>• Investment_Goals [Multiple selections allowed]</li>
                  <li>• Risk_Tolerance [Conservative/Moderate/Aggressive]</li>
                  <li>• Annual_Income_Range [$XX,XXX-$XX,XXX]</li>
                  <li>• Net_Worth [$XXX,XXX]</li>
                  <li>• Portfolio_Value [$XXX,XXX]</li>
                  <li>• Tax_Filing_Status [Single/Married_Joint/Married_Separate/Head_of_Household]</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-md font-medium text-gray-800 mb-2">4. Relationship Data</h3>
                <ul className="space-y-1 text-gray-700">
                  <li>• Financial_Advisor_Name</li>
                  <li>• Last_Contact_Date [MM/DD/YYYY]</li>
                  <li>• Next_Follow_Up [MM/DD/YYYY]</li>
                  <li>• Client_Status [Active/Prospect/Inactive/Closed]</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Optional Fields</h2>
            <ul className="space-y-2 text-gray-700">
              <li>• Beneficiaries [Comma-separated names]</li>
              <li>• Account_Roles [Primary/Secondary/Trustee/Custodian]</li>
              <li>• Tags [Max 5, comma-separated]</li>
              <li>• Client_Notes [500 char max]</li>
            </ul>
            
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Data Import Guidelines</h2>
            <ol className="space-y-2 list-decimal pl-5 text-gray-700">
              <li>Save as .CSV format only</li>
              <li>Ensure all required fields (*) are completed</li>
              <li>Follow specified date format: MM/DD/YYYY</li>
              <li>Include $ and commas in all currency values</li>
              <li>Use provided drop-down menus for selection fields</li>
              <li>Verify data accuracy before submission</li>
            </ol>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">Client_ID*</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">First_Name*</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">Last_Name*</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">Email*</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">Phone*</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">Client_Status*</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">FA12345</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">John</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">Smith</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">john.smith@example.com</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">555-123-4567</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">Active</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">* Sample data shown. Full template contains all fields listed above.</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Important Note</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Before importing, ensure all client data complies with your organization's data privacy policies and regulatory requirements.
                Sensitive financial information should be handled according to applicable regulations.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <Button
            onClick={handleDownload}
            disabled={isDownloading}
            className="py-3 px-8 text-lg"
          >
            {isDownloading ? (
              <>
                <span className="loader mr-2"></span>
                Downloading...
              </>
            ) : (
              <>
                <Download className="h-5 w-5 mr-2" />
                Download Template
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FinancialAdvisorCRMTemplate;